import React, { Component, Fragment } from 'react';
import {
	IconButton,
	IconV2,
	Nub,
	TextField,
	LayoutBox,
} from '@bamboohr/fabric';
import { Select as ReactSelect } from '@fabric/select';
import './style.styl';
import { classNameFromObject as co } from '@utils/dom';
import Tooltip from 'tooltip.react';

export class TheNewEmployeeSelector extends Component {
	constructor(props) {
		super(props);

		let { employees, isRowSelected, sort } = this.props;

		this.leftSideUsedLast = false;
		this.lastRowKeyUsed = null;

		employees = employees.sort(sort);
		const [selected, unselected] = splitOnCondition(employees, isRowSelected);

		this.state = {
			availableEmployees: unselected,
			selectedEmployees: selected,
			firstFilter: null,
			secondFilter: null,
			showSecondFilter: false,
			searchFocused: false,
			searchValue: '',
			leftHighlightedKeys: {},
			rightHighlightedKeys: {},
		};
	}

	static defaultProps = {
		isRowDisabled: (o) => o.disabled,
		isRowSelected: (o) => o.selected,
		mainContent: (o) => o.lastNameFirstName,
		rowKey: (o) => o.id,
		rowDisabledTooltip: () => null,
		secondaryContent: () => null,
		secondaryContentIcon: () => null,
		showSearch: true,
		sort: (a, b) => {
			const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
			if (lastNameCompare !== 0) {
				return lastNameCompare;
			}
			return (a.firstName || '').localeCompare(b.firstName || '');
		}
	};

	_filterAndPlace = () => {
		let { employees, rowKey, isRowSelected, sort } = this.props;
		const { leftHighlightedKeys } = this.state;

		employees = employees.sort(sort);
		const [selected, unselected] = splitOnCondition(employees, isRowSelected);

		const newLeftHighlightedKeys = {};

		const filtered = unselected.filter(this._isNotFiltered);
		filtered.forEach((employee) => {
			const key = rowKey(employee);
			if (leftHighlightedKeys[key]) {
				newLeftHighlightedKeys[key] = true;
			}
		});

		this.setState({
			availableEmployees: filtered,
			selectedEmployees: selected,
			leftHighlightedKeys: newLeftHighlightedKeys,
		});
	};

	_sortFilters = (a, b) => {
		if (a.text && b.text) {
			const nameA = a.text.toUpperCase();
			const nameB = b.text.toUpperCase();
			if (nameA < nameB) {
				return -1;
			}
			if (nameA > nameB) {
				return 1;
			}
		}
		return 0;
	};

	_createFirstFilterSettings = () => {
		const { filters } = this.props;
		const { firstFilter, showSecondFilter } = this.state;

		const widthWithoutSecondFilter = 100;

		const settings = {
			onClear: this._handleFirstFilterCleared,
			onSelect: this._handleFirstFilterSelect,
			placeholder: $.__('All Employees'),
			showSearch: 'never',
			size: 'small',
			width: showSecondFilter ? 4 : widthWithoutSecondFilter,
		};

		if (firstFilter) {
			settings.items = firstFilter.items;
			settings.selectedValues = firstFilter.selectedValues;
		} else {
			settings.items = filters.map((filter) => {
				const { key, name, options } = filter;
				return {
					key,
					text: name,
					items: Object.keys(filter.options)
						.reduce((arr, key) => {
							arr.push({
								text: options[key],
								value: key,
							});
							return arr;
						}, [])
						.sort(this._sortFilters),
				};
			});
		}
		return settings;
	};

	_createSecondFilterSettings = () => {
		const { filters } = this.props;
		const { firstFilter, secondFilter } = this.state;

		const settings = {
			onClear: this._handleSecondFilterCleared,
			onSelect: this._handleSecondFilterSelect,
			placeholder: $.__('-- Select --'),
			showSearch: 'never',
			size: 'small',
			width: 4,
		};

		if (secondFilter) {
			settings.items = secondFilter.items;
			settings.selectedValues = secondFilter.selectedValues;
		} else {
			settings.items = filters
				.filter((filter) => filter.key !== firstFilter.parentKey)
				.map((filter) => {
					const { key, name, options } = filter;
					return {
						key,
						text: name,
						items: Object.keys(filter.options)
							.reduce((arr, key) => {
								arr.push({
									text: options[key],
									value: key,
								});
								return arr;
							}, [])
							.sort(this._sortFilters),
					};
				});
		}

		return settings;
	};

	_createRowMeta = (row) => {
		const {
			rowKey,
			mainContent,
			secondaryContent,
			secondaryContentIcon,
			isRowDisabled,
			rowDisabledTooltip,
		} = this.props;

		return {
			key: rowKey(row),
			main: mainContent(row),
			secondary: secondaryContent(row),
			secondaryIcon: secondaryContentIcon(row),
			isDisabled: isRowDisabled(row),
			disabledTooltipContent: rowDisabledTooltip(row),
		};
	};

	_isNotFiltered = (row) => {
		const { firstFilter, secondFilter, searchValue } = this.state;
		const value = searchValue.toLowerCase().trim();

		const matchesFirstFilter = firstFilter
			? this._checkFilterMatch(
					row,
					firstFilter.parentKey,
					firstFilter.selectedValues[0],
			  )
			: true;
		if (!matchesFirstFilter) {
			return false;
		}

		const matchesSecondFilter = secondFilter
			? this._checkFilterMatch(
					row,
					secondFilter.parentKey,
					secondFilter.selectedValues[0],
			  )
			: true;
		if (!matchesSecondFilter) {
			return false;
		}

		const matchesSearchInput =
			row.firstName?.toLowerCase().indexOf(value) === 0 ||
			row.lastName?.toLowerCase().indexOf(value) === 0 ||
			row.displayName?.toLowerCase().indexOf(value) === 0 ||
			row.lastNameFirstName?.toLowerCase().indexOf(value) === 0;
		if (!matchesSearchInput) {
			return false;
		}

		return true;
	};

	_checkFilterMatch = (row, key, value) => {
		// Handle the case where the field is an array (like teamAssignments)
		if (key === 'teamAssignments' && Array.isArray(row[key])) {
			// Check if any value in the array matches the filter value
			return row[key].some((assignment) => String(assignment) === value);
		}

		// Default behavior for regular key/value pairs
		return String(row[key]) === value;
	};

	_handleFirstFilterSelect = (newValue, notSureWhatThisIsFor, details) => {
		const { firstFilter } = this.state;

		this.setState(
			{
				firstFilter: {
					parentKey: details.parentItem
						? details.parentItem.key
						: firstFilter.parentKey,
					items: details.parentItem
						? details.parentItem.items
						: firstFilter.items,
					selectedValues: [newValue],
				},
			},
			this._filterAndPlace,
		);
	};

	_handleFirstFilterCleared = () => {
		const { secondFilter } = this.state;
		const firstFilter = secondFilter ? secondFilter : null;

		this.setState(
			{
				firstFilter,
				secondFilter: null,
				showSecondFilter: false,
			},
			this._filterAndPlace,
		);
	};

	_handleSecondFilterSelect = (newValue, notSureWhatThisIsFor, details) => {
		const { secondFilter } = this.state;

		this.setState(
			{
				secondFilter: {
					parentKey: details.parentItem
						? details.parentItem.key
						: secondFilter.parentKey,
					items: details.parentItem
						? details.parentItem.items
						: secondFilter.items,
					selectedValues: [newValue],
				},
			},
			this._filterAndPlace,
		);
	};

	_handleSecondFilterCleared = () => {
		this.setState(
			{
				secondFilter: null,
				showSecondFilter: false,
			},
			this._filterAndPlace,
		);
	};

	_handleAddFilter = () => {
		this.setState({
			showSecondFilter: true,
		});
	};

	_handleSearchFocus = () => {
		this.setState({
			searchFocused: true,
		});
	};

	_handleSearchBlur = () => {
		this.setState({
			searchFocused: false,
		});
	};

	_handleSearchChange = (event) => {
		this.setState(
			{
				searchValue: event.target.value,
			},
			this._filterAndPlace,
		);
	};

	_handleRowClick = (event, leftSide, rowKey, isDisabled, isHighlighted) => {
		const { rowKey: makeKey, isRowDisabled } = this.props;
		const { availableEmployees, selectedEmployees } = this.state;

		if (isDisabled) {
			return;
		}

		const { leftHighlightedKeys, rightHighlightedKeys } = this.state;
		let highlightedKeys = leftSide ? leftHighlightedKeys : rightHighlightedKeys;
		let employees = leftSide ? availableEmployees : selectedEmployees;

		if (
			event.shiftKey &&
			this.lastRowKeyUsed &&
			this.lastRowKeyUsed !== rowKey &&
			this.leftSideUsedLast === leftSide
		) {
			const changed = [];
			let pushing = false;

			for (let i = 0; i < employees.length; i++) {
				const key = makeKey(employees[i]);
				const disabled = isRowDisabled(employees[i]);

				if (!disabled && pushing) {
					changed.push(key);
				}

				if (!disabled && (key === rowKey || key === this.lastRowKeyUsed)) {
					// eslint-disable-next-line max-depth
					if (pushing) {
						break;
					}
					changed.push(key);
					pushing = true;
				}
			}

			changed.forEach((change) => {
				highlightedKeys[change] = !isHighlighted;
			});
		} else if (event.metaKey || event.altKey || event.ctrlKey) {
			highlightedKeys[rowKey] = !isHighlighted;
		} else {
			highlightedKeys = { [rowKey]: true };
		}

		this.lastRowKeyUsed = rowKey;
		this.leftSideUsedLast = leftSide;

		this.setState({
			[leftSide ? 'leftHighlightedKeys' : 'rightHighlightedKeys']:
				highlightedKeys,
		});
	};

	_onClickAddAll = () => {
		const { onSelect, isRowDisabled } = this.props;
		const { availableEmployees, leftHighlightedKeys } = this.state;
		const records = availableEmployees.filter((row) => !isRowDisabled(row));

		if (records.length === 0) {
			return;
		}

		onSelect(records);
		this.setState({
			leftHighlightedKeys: {},
			rightHighlightedKeys: leftHighlightedKeys,
		});
	};

	_onClickAddHighlighted = () => {
		const { onSelect, isRowDisabled, rowKey } = this.props;
		const { availableEmployees, leftHighlightedKeys } = this.state;
		const records = availableEmployees.filter(
			(row) => !isRowDisabled(row) && leftHighlightedKeys[rowKey(row)],
		);

		if (records.length === 0) {
			return;
		}

		onSelect(records);
		this.setState({
			leftHighlightedKeys: {},
			rightHighlightedKeys: leftHighlightedKeys,
		});
	};

	_onClickRemoveHighlighted = () => {
		const { onDeselect, isRowDisabled, rowKey } = this.props;
		const { selectedEmployees, rightHighlightedKeys } = this.state;
		const records = selectedEmployees.filter(
			(row) => !isRowDisabled(row) && rightHighlightedKeys[rowKey(row)],
		);

		if (records.length === 0) {
			return;
		}

		onDeselect(records);
		this.setState({
			rightHighlightedKeys: {},
			leftHighlightedKeys: rightHighlightedKeys,
		});
	};

	_onClickRemoveAll = () => {
		const { onDeselect, isRowDisabled } = this.props;
		const { rightHighlightedKeys, selectedEmployees } = this.state;
		const records = selectedEmployees.filter((row) => !isRowDisabled(row));

		if (records.length === 0) {
			return;
		}

		onDeselect(records);
		this.setState({
			rightHighlightedKeys: {},
			leftHighlightedKeys: rightHighlightedKeys,
		});
	};

	componentDidUpdate(prevProps, prevState, snapshot) {
		const { employees } = this.props;

		if (employees !== prevProps.employees) {
			this._filterAndPlace();
		}
	}

	render() {
		let { filters = [], rowDisabledTooltip, showSearch = true } = this.props;

		const {
			availableEmployees,
			selectedEmployees,
			leftHighlightedKeys,
			rightHighlightedKeys,
			searchFocused,
			searchValue,
			firstFilter,
			showSecondFilter,
		} = this.state;

		return (
			<div className="EmployeeSelectorReact">
				<div className="EmployeeSelectorReact__container">
					<div className="EmployeeSelectorReact__title">
						{$.__('Available Employees')}
					</div>
					<div className="EmployeeSelectorReact__wrapper">
						{filters.length > 0 && (
							<div className="EmployeeSelectorReact__selects">
								<ReactSelect {...this._createFirstFilterSettings()} />
								{!!firstFilter && !showSecondFilter && (
									<div
										className="EmployeeSelectorReact__addSelectIcon"
										onClick={this._handleAddFilter}
									>
										<IconV2 name="circle-plus-regular" size={14} />
									</div>
								)}
								{showSecondFilter && (
									<Fragment>
										<div className="EmployeeSelectorReact__plusSelectIcon">
											+
										</div>
										<ReactSelect {...this._createSecondFilterSettings()} />
									</Fragment>
								)}
							</div>
						)}

						{showSearch && (
							<div className="EmployeeSelectorReact__searchContainer">
								<LayoutBox paddingTop={filters.length > 0 ? '0px' : '16px'}>
									<TextField
										onBlur={this._handleSearchBlur}
										onChange={this._handleSearchChange}
										onFocus={this._handleSearchFocus}
										placeholder={$.__('Search...')}
										value={searchValue}
										size={'small'}
										InputProps={{
											startAdornment: (
												<Nub position={'start'}>
													<IconV2
														name="magnifying-glass-solid"
														size={16}
														color={'neutral-weak'}
													/>
												</Nub>
											),
										}}
									/>
								</LayoutBox>
							</div>
						)}

						{(firstFilter || searchValue) &&
							availableEmployees.length === 0 && (
								<div className="EmployeeSelectorReact__noFilterMatchBlankState">
									{$.__('No employees match this filter.')}
								</div>
							)}

						<div className="EmployeeSelectorReact__listContainer">
							{availableEmployees.length > 0 && (
								<ul className="EmployeeSelectorReact__list">
									{availableEmployees.map((row) => {
										const {
											key,
											main,
											secondary,
											secondaryIcon,
											isDisabled,
											disabledTooltipContent,
										} = this._createRowMeta(row);
										const isHighlighted = leftHighlightedKeys[key];

										return (
											<ConditionalWrap
												condition={isDisabled && disabledTooltipContent}
												key={key}
												wrap={(children) => (
													<Tooltip
														settings={{ content: disabledTooltipContent }}
													>
														{' '}
														{children}{' '}
													</Tooltip>
												)}
											>
												<li
													className={co('EmployeeSelectorReact__listItem', {
														'EmployeeSelectorReact__listItem--disabled':
															isDisabled,
														'EmployeeSelectorReact__listItem--highlighted':
															isHighlighted,
													})}
													onClick={(event) =>
														this._handleRowClick(
															event,
															true,
															key,
															isDisabled,
															isHighlighted,
														)
													}
												>
													<div className="EmployeeSelectorReact__listItem__mainContent">
														{main}
													</div>
													{secondary && (
														<span
															className={co(
																'EmployeeSelectorReact__listItem__textRight',
																{
																	'EmployeeSelectorReact__listItem__textRight--highlighted':
																		isHighlighted,
																},
															)}
														>
															{secondaryIcon && (
																<span
																	className={co(
																		'EmployeeSelectorReact__listItem__textRightIcon',
																		{
																			'EmployeeSelectorReact__listItem__textRightIcon--highlighted':
																				isHighlighted,
																		},
																	)}
																>
																	<IconV2
																		color="neutral-medium"
																		name={secondaryIcon}
																		size={12}
																	/>
																</span>
															)}
															{secondary}
														</span>
													)}
												</li>
											</ConditionalWrap>
										);
									})}
								</ul>
							)}
						</div>
					</div>
				</div>

				<div className="EmployeeSelectorReact__buttons">
					<div className="EmployeeSelectorReact__btn">
						<IconButton
							ariaLabel={$.__('All Employees')}
							icon={
								<IconV2
									color={'neutral-x-strong'}
									name={'chevrons-right-regular'}
									size={16}
								/>
							}
							onClick={this._onClickAddAll}
							size="small"
							type="button"
							variant="outlined"
						/>
					</div>
					<div className="EmployeeSelectorReact__btn">
						<IconButton
							ariaLabel={$.__('Select employee')}
							icon={
								<IconV2
									color={'neutral-x-strong'}
									name={'chevron-right-regular'}
									size={16}
								/>
							}
							onClick={this._onClickAddHighlighted}
							size="small"
							type="button"
							variant="outlined"
						/>
					</div>
					<div className="EmployeeSelectorReact__btn">
						<IconButton
							ariaLabel={$.__('Remove employee')}
							icon={
								<IconV2
									color={'neutral-x-strong'}
									name={'chevron-left-regular'}
									size={16}
								/>
							}
							onClick={this._onClickRemoveHighlighted}
							size="small"
							type="button"
							variant="outlined"
						/>
					</div>
					<div className="EmployeeSelectorReact__btn">
						<IconButton
							ariaLabel={$.__('Remove all employees')}
							icon={
								<IconV2
									color={'neutral-x-strong'}
									name={'chevrons-left-regular'}
									size={16}
								/>
							}
							onClick={this._onClickRemoveAll}
							size="small"
							type="button"
							variant="outlined"
						/>
					</div>
				</div>

				<div className="EmployeeSelectorReact__container">
					<p className="EmployeeSelectorReact__title">
						{$.__('Selected Employees')}
					</p>
					<div className="EmployeeSelectorReact__wrapper">
						{selectedEmployees.length > 0 && (
							<div className="EmployeeSelectorReact__listContainer">
								<ul className="EmployeeSelectorReact__list">
									{selectedEmployees.map((row) => {
										const { key, main, secondary, isDisabled } =
											this._createRowMeta(row);
										const isHighlighted = rightHighlightedKeys[key];

										return (
											<li
												className={co('EmployeeSelectorReact__listItem', {
													'EmployeeSelectorReact__listItem--disabled':
														isDisabled,
													'EmployeeSelectorReact__listItem--highlighted':
														isHighlighted,
												})}
												key={key}
												onClick={(event) =>
													this._handleRowClick(
														event,
														false,
														key,
														isDisabled,
														isHighlighted,
													)
												}
											>
												<div className="EmployeeSelectorReact__listItem__mainContent">
													{main}
												</div>
												{secondary && (
													<span
														className={co(
															'EmployeeSelectorReact__listItem__textRight',
															{
																'EmployeeSelectorReact__listItem__textRight--highlighted':
																	isHighlighted,
															},
														)}
													>
														{' '}
														{secondary}
													</span>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						)}

						{selectedEmployees.length === 0 && (
							<div className="EmployeeSelectorReact__rightBlankState">
								<div className="EmployeeSelectorReact__rightBlankStateIcon">
									<IconV2
										color={'neutral-medium'}
										name="circle-user-regular"
										size={60}
									/>
								</div>
								<p className="EmployeeSelectorReact__rightBlankState__text">
									{$.__(`You haven't added any employees yet.`)}
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
}

function ConditionalWrap({ condition, wrap, children }) {
	return condition ? wrap(children) : children;
}

function splitOnCondition(array, condition) {
	const metCondition = [];
	const failedCondition = [];

	for (let i = 0; i < array.length; i++) {
		const item = array[i];
		(condition(item) ? metCondition : failedCondition).push(item);
	}

	return [metCondition, failedCondition];
}
